import * as STD from '@emersya/mpa-std-lib';

import CM                                     from '@emersya/mpa-components-manager-lib';
import { Link, Image }                        from '@emersya/mpa-components-lib';

export interface IPlannerFurnitureComponentConfiguration extends CM.DOMComponent.IConfiguration {
	imgSource : string;
	linkSource : Record<string,STD.translationsManager.TranslationMapping>;
};

export class PlannerFurnitureComponent extends CM.DOMComponent<
	HTMLDivElement,
	PlannerFurnitureComponent,
	IPlannerFurnitureComponentConfiguration
>
{
	static override initialize() {
		super.initialize();
	};

	component_DOM : HTMLDivElement;

	get categories() {
		const menu = this.component_find('@:menu');
		return <PlannerFurnitureCategoryTile.Component[]> menu.component_outerChilds;
	};

	getWDH() {
		const p = app.currentProduct;
		if (!p) {
			this.component_throwUserError('');
		}
		const dpart = p.getPart([ 'dimensions' ]);
		return {
			width :  Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1),
			depth :  Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1),
			height : Number.parseFloat(dpart.getPart([ 'height' ])?.reference?.value || <string> <any> 1)
		};
	};

	async changeLanguageLink() {
		const link = <Link.Component> this.component_find('@:linkPDF');
		const product = app.currentProduct;
		if(!product || !product.id){
			return;
		}
		const linkSource = this.component_configuration.linkSource[app.currentProduct.id];
		if(!linkSource){
			this.component_throwUserError('Missing PDF for product : '+product.id);
		}
		let newHref = linkSource[ app.currentLanguage ];
		const pdfButton = this.component_find('@:linkPDF');
		const pdfTitle = this.component_find('@:pdfDisclaimer');
		if (!newHref || newHref === '') {
			newHref = '';
			if(!pdfButton.component_isDisabled){
				await pdfButton.component_disable();
			}
			if(!pdfTitle.component_isDisabled){
				await pdfTitle.component_disable();
			}
		}else{
			if(pdfButton.component_isDisabled){
				await pdfButton.component_enable();
			}
			if(pdfTitle.component_isDisabled){
				await pdfTitle.component_enable();
			}
		}
		link.updateHref(newHref);
	}

	async updateDisclaimer() {
		const product = app.currentProduct;
		const wdh = this.getWDH();
		const surface = wdh.depth * wdh.width;
		if (!product) {
			this.component_throwUserError('');
		}
		const disclaimerTitle = this.component_find('@:disclaimerTitle');
		const disclaimerSIAE = this.component_find('@:disclaimerSIAE');
		const disclaimerBusinessSmall = this.component_find('@:disclaimerBusinessSmall');
		const disclaimerBusinessMedium = this.component_find('@:disclaimerBusinessMedium');
		const disclaimerBusinessLarge = this.component_find('@:disclaimerBusinessLarge');
		const disclaimerBusinessExtraLarge = this.component_find('@:disclaimerBusinessExtraLarge');
		if (product.id === 'siae') {
			await disclaimerTitle.component_enable();
			await disclaimerBusinessSmall.component_disable();
			await disclaimerBusinessMedium.component_disable();
			await disclaimerBusinessLarge.component_disable();
			await disclaimerBusinessExtraLarge.component_disable();
			await disclaimerSIAE.component_enable();
		} else if (product.id === 'business') {
			if (surface >= 36) {
				await disclaimerBusinessSmall.component_disable();
				await disclaimerBusinessMedium.component_disable();
				await disclaimerBusinessLarge.component_disable();
				await disclaimerBusinessExtraLarge.component_enable();
			} else if (surface > 25) {
				await disclaimerBusinessSmall.component_disable();
				await disclaimerBusinessMedium.component_disable();
				await disclaimerBusinessLarge.component_enable();
				await disclaimerBusinessExtraLarge.component_disable();
			} else if (surface > 15) {
				await disclaimerBusinessSmall.component_disable();
				await disclaimerBusinessMedium.component_enable();
				await disclaimerBusinessLarge.component_disable();
				await disclaimerBusinessExtraLarge.component_disable();
			} else {
				await disclaimerBusinessSmall.component_enable();
				await disclaimerBusinessMedium.component_disable();
				await disclaimerBusinessLarge.component_disable();
				await disclaimerBusinessExtraLarge.component_disable();
			}
			await disclaimerTitle.component_enable();
			await disclaimerSIAE.component_disable();
		} else {
			await disclaimerTitle.component_disable();
			await disclaimerBusinessSmall.component_disable();
			await disclaimerBusinessMedium.component_disable();
			await disclaimerBusinessLarge.component_disable();
			await disclaimerBusinessExtraLarge.component_disable();
			await disclaimerSIAE.component_disable();
		}
	}

	override async component_initialize() {
		const menu = this.component_find('@:menu');
		const furnitureComponents = app.component_configuration.productsComponents.filter(x => x.type === '@furniture');
		const productsInFunctionOfCategory = furnitureComponents.reduce<Record<string, typeof app.component_configuration.productsComponents>>(
			(a, c) => {
				if (!a[ c.category ]) {
					a[ c.category ] = [];
				}
				const store = a[ c.category ];
				if (!store) {
					throw new Error('!'); // Very bad, is impossible!!!
				}
				store.push(c);
				return a;
			},
			{}
		);
		const categoriesId = Array.from(new Set(furnitureComponents.map(x => x.category)));
		for (const categoryId of categoriesId) {
			const categoryConfiguration = app.component_configuration.productsComponentsCategories[ categoryId ];
			if (!categoryConfiguration) {
				this.component_throwUserError('');
			}
			const productsConfigurations = productsInFunctionOfCategory[ categoryId ];
			if (!productsConfigurations) {
				this.component_throwLibraryError('');
			}
			const categoryComponent = <CM.DOMComponent<any, any, any>> await menu.component_instantiateOuterChild<PlannerFurnitureCategoryTile.Configuration>(
				{
					type :          'viewer-planner-furniture-categoryTile',
					id :            categoryId,
					tree :          this.component_tree,
					slot :          null,
					isSlot :        false,
					declaredSlot :  null,
					configuration : {
						categoryConfiguration,
						productsConfigurations
					}
				}
			);
			void categoryComponent;
		}
		await super.component_initialize();
	};

	/**
	 * Disable unused categories and enable used ones
	 * Update categories.
	 **/
	async update() {
		await this.updateDisclaimer();
		for (const category of this.categories) {
			await category.update();
		}
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IPlannerFurnitureComponentConfiguration
	)
	{
		const trIcon = STD.translationsManager.languages.reduce<Record<string, string>>((a, c) => {
			a[ c ] = 'iconPDF';
			return a;
		}, {});
		configuration[ '@innerConfigurations' ] = {
			linkPDF : <Link.Configuration> {
				href :                   '',
				target :                 '_blank',
				'@translations' :        {},
				'@innerConfigurations' : {
					image : <Image.Configuration> {
						URL :             configuration.imgSource,
						'@translations' : {
							'name' : trIcon
						}
					}
				}
			}
		};
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({
			tag : 'div'
		});
	};
};
export namespace NS {
	export const Component = PlannerFurnitureComponent;
	export type Component = PlannerFurnitureComponent;
	export type Configuration = IPlannerFurnitureComponentConfiguration;
}
;
import manifestXMLSource                      from './PlannerFurnitureEditor.xml';
import './PlannerFurnitureEditor.scss';
import { NS as PlannerFurnitureCategoryTile } from '../CategoryTile/PlannerFurnitureCategoryTile';

CM.declare({
	id :       'viewer-planner-furniture-editor',
	type :     PlannerFurnitureComponent,
	manifest : manifestXMLSource
});
